'use client';

import AppImage from '@/components/ui/AppImage';
import RegistrationForm from './RegistrationForm';

const FLOATING_AVATARS = [
{ src: "https://images.unsplash.com/photo-1505296180394-285ca267d904", alt: 'Junge Frau lächelt in die Kamera', name: 'Laura, 28', city: 'Berlin', delay: '0s' },
{ src: "https://images.unsplash.com/photo-1609231403345-e0a6e89512cd", alt: 'Mann mit freundlichem Lächeln', name: 'Markus, 34', city: 'München', delay: '1.5s' },
{ src: "https://images.unsplash.com/photo-1696865274650-c98f7a51874a", alt: 'Frau mit braunen Haaren lächelt', name: 'Sophie, 26', city: 'Hamburg', delay: '0.8s' }];


export default function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center pt-20 pb-12 overflow-hidden animated-gradient">
      {/* Background blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="blob-1 absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(232,49,90,0.22) 0%, transparent 70%)' }} />
        <div className="blob-2 absolute -bottom-40 -right-40 w-[800px] h-[800px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(255,107,53,0.16) 0%, transparent 70%)' }} />
        <div className="absolute top-1/3 left-1/2 w-[500px] h-[500px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(139,26,92,0.14) 0%, transparent 70%)', transform: 'translate(-50%,-50%)' }} />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 w-full py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">

          {/* Left: Headline + Social Proof */}
          <div className="lg:col-span-6 xl:col-span-7 space-y-10">
            {/* Badge */}
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(232,49,90,0.12)', border: '1px solid rgba(232,49,90,0.3)', color: '#FF8CA8', boxShadow: '0 4px 16px rgba(232,49,90,0.2)' }}>
              <span className="w-2.5 h-2.5 rounded-full online-dot" style={{ background: '#E8315A' }} />
              Über 2.4 Mio. aktive Singles in DACH
            </div>

            {/* Headline */}
            <div className="space-y-4">
              <h1 className="hero-headline text-white">
                Finde dein<br />
                <span className="text-gradient italic">perfektes Match</span><br />
                in deiner Nähe.
              </h1>
            </div>

            {/* Sub */}
            <p className="text-xl font-light max-w-md leading-relaxed" style={{ color: 'rgba(245,240,247,0.65)' }}>
              Echte Menschen, echte Gespräche. Melde dich in 2 Minuten kostenlos an und entdecke Singles aus deiner Region.
            </p>

            {/* Social proof avatars */}
            <div className="flex items-center gap-5">
              <div className="flex -space-x-3">
                {FLOATING_AVATARS?.map((a, i) =>
                <div key={i} className="w-12 h-12 rounded-full overflow-hidden border-[3px] transition-transform hover:scale-110 hover:z-10"
                style={{ borderColor: 'rgba(232,49,90,0.5)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
                    <AppImage src={a?.src} alt={a?.alt} width={48} height={48} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="w-12 h-12 rounded-full border-[3px] flex items-center justify-center text-sm font-bold transition-transform hover:scale-110"
                style={{ borderColor: 'rgba(232,49,90,0.5)', background: 'rgba(232,49,90,0.18)', color: '#FF8CA8', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
                  +2M
                </div>
              </div>
              <div>
                <div className="flex gap-0.5 mb-1.5">
                  {[...Array(5)]?.map((_, i) =>
                  <span key={i} className="text-base" style={{ color: '#FF6B35', filter: 'drop-shadow(0 2px 4px rgba(255,107,53,0.3))' }}>★</span>
                  )}
                </div>
                <p className="text-sm font-medium" style={{ color: 'rgba(245,240,247,0.55)' }}>
                  4.8/5 von über 48.000 Bewertungen
                </p>
              </div>
            </div>

            {/* Floating member cards — desktop only */}
            <div className="hidden lg:flex gap-5 mt-6">
              {FLOATING_AVATARS?.map((a, i) =>
              <div key={i} className="float-card glass-card rounded-2xl p-4 flex items-center gap-3 hover:scale-105 transition-all"
              style={{ animationDelay: a?.delay }}>
                  <div className="relative">
                    <div className="w-11 h-11 rounded-full overflow-hidden">
                      <AppImage src={a?.src} alt={a?.alt} width={44} height={44} className="w-full h-full object-cover" />
                    </div>
                    <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-[2.5px]"
                  style={{ background: '#22C55E', borderColor: '#161219', boxShadow: '0 0 8px rgba(34,197,94,0.5)' }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{a?.name}</p>
                    <p className="text-xs" style={{ color: 'rgba(245,240,247,0.5)' }}>{a?.city}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Registration Form */}
          <div className="lg:col-span-6 xl:col-span-5" id="registration-form">
            <div className="glass-card rounded-3xl p-7 md:p-9 relative overflow-hidden hover:border-primary/20 transition-all">
              {/* Diagonal rays overlay */}
              <div className="absolute inset-0 diagonal-rays opacity-30 pointer-events-none rounded-3xl" />
              <div className="relative z-10">
                <div className="mb-7">
                  <h2 className="font-display text-3xl font-bold text-white mb-2">
                    Kostenlos anmelden
                  </h2>
                  <p className="text-sm" style={{ color: 'rgba(245,240,247,0.5)' }}>
                    In 3 Schritten zu deinem Profil — dauert nur 2 Minuten.
                  </p>
                </div>
                <RegistrationForm />
              </div>
            </div>
          </div>

        </div>
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 scroll-indicator">
        <span className="text-xs font-medium" style={{ color: 'rgba(245,240,247,0.3)' }}>Scroll</span>
        <div className="w-px h-10" style={{ background: 'linear-gradient(to bottom, rgba(232,49,90,0.6), transparent)' }} />
      </div>
    </section>);

}